import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSEO } from "@/hooks/use-seo";
import { Shield, Users, FileText, BarChart3, CheckCircle2, Zap, Globe, Clock } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PricingSection from "@/components/landing/PricingSection";
import Footer from "@/components/layout/Footer";
const Index: React.FC = () => {
  usePageSEO("Zoocertica – Professional Wildlife Facility Management", "Comprehensive wildlife facility management platform with integrated compliance, animal tracking, and permit management.", "/");
  const {
    isAuthenticated,
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const features = [{
    icon: Users,
    title: "Animal Management",
    description: "Track health records, inventory, and individual animal histories across multiple facilities with complete audit trails"
  }, {
    icon: Shield,
    title: "Permit & Compliance",
    description: "Centralized CITES, USDA, and state permit tracking with automated expiration alerts"
  }, {
    icon: FileText,
    title: "Documentation Hub",
    description: "Store veterinary records, transport certificates, inspection reports, and generate audit-ready exports on demand"
  }, {
    icon: BarChart3,
    title: "Legal Research",
    description: "AI-Assisted Regulation Lookup and transport requirements to ensure regulatory compliance"
  }];
  const benefits = ["CITES permit tracking", "USDA license management", "State-by-state legality summaries", "Veterinary record system", "Inspection scheduling", "Transport certificates", "Audit-ready exports", "Multi-facility support"];
  return <div className="min-h-screen bg-background">
      <PublicHeader />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative px-2 py-6 sm:px-4 sm:py-8 md:py-12 lg:py-16 lg:px-8 xl:py-[20px]">
          <div className="mx-auto max-w-4xl text-center">
            <div className="animate-fade-in">
              <div className="flex flex-col items-center justify-center">
                <img src="/zoocertica_paw_thin-2.png" alt="ZooCertica Logo" className="h-32 w-32 sm:h-48 sm:w-48 md:h-64 md:w-64 lg:h-80 lg:w-80 xl:h-96 xl:w-96 object-contain max-w-full h-auto" width="384" height="205" fetchPriority="high" loading="eager" decoding="async" />
              </div>
              <p className="text-base sm:text-lg md:text-xl leading-6 text-white/90 px-2 sm:px-4 mt-4">
                Comprehensive Compliance Platform for Zoos & Sanctuaries
              </p>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-white/80 max-w-2xl mx-auto px-2 sm:px-4">
                Manage CITES and USDA permits, track veterinary records, automate transport and legal checks, and maintain full facility compliance — all in one secure dashboard. Private collector tools included.
              </p>
              
              {/* Free Trial Notification */}
              {!isAuthenticated && <div className="mt-4 sm:mt-6 flex items-center justify-center">
                  <button onClick={() => navigate("/auth")} className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 flex items-center gap-2 hover:bg-white/20 transition-all duration-300 cursor-pointer">
                    <Clock className="h-4 w-4 text-white" />
                    <span className="text-sm sm:text-base text-white font-medium">
                      Start your 14-day free trial - no commitment required
                    </span>
                  </button>
                </div>}
            </div>
            
            <div className="mt-6 sm:mt-8 md:mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-4 md:gap-6 animate-slide-up px-2 sm:px-4">
              {isAuthenticated ? <Button onClick={() => navigate("/admin")} size="lg" className="bg-white text-primary hover:bg-white/90 shadow-elegant w-full sm:w-auto">
                  Go to Dashboard
                </Button> : <>
                  <Button onClick={() => navigate("/auth?tab=signup")} size="lg" className="bg-white text-primary hover:bg-white/90 shadow-elegant w-full sm:w-auto">
                    Create Account
                  </Button>
                  <Button onClick={() => navigate("/auth")} variant="outline" size="lg" className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm w-full sm:w-auto">
                    Sign In
                  </Button>
                </>}
              <Button variant="outline" onClick={() => navigate("/legal-lookup")} size="lg" className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm w-full sm:w-auto">
                <Globe className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">State Regulations</span>
                <span className="sm:hidden">Regulations</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-background lg:py-[40px]">
        <div className="mx-auto max-w-7xl px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Built for Responsible Zoos, Sanctuaries, and Accredited Facilities
            </h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-muted-foreground px-2">
              Consolidate permit tracking, animal records, and compliance workflows in one centralized platform. From CITES and USDA oversight to transport logistics and audit-ready exports, Zoocertica keeps your facility inspection-ready and fully compliant.
            </p>
          </div>
          
          <div className="mx-auto mt-8 sm:mt-12 md:mt-16 max-w-7xl">
            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => <Card key={index} className="text-center border-0 shadow-elegant hover:shadow-glow transition-all duration-300">
                  <CardHeader className="p-3 sm:p-4 md:p-6">
                    <div className="mx-auto h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-primary p-2 sm:p-3">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg sm:text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                    <CardDescription className="text-sm sm:text-base">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Collector Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="mx-auto max-w-7xl px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              🐍 Also for Responsible Reptile & Aquatic Keepers
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground px-2">
              Zoocertica now includes a dedicated plan for licensed private collectors. 
              Manage your collection, track feedings, monitor habitats, and create official transfer certificates — 
              all with the same reliability trusted by zoos and sanctuaries.
            </p>
            <div className="mt-6 sm:mt-8">
              <Button onClick={() => navigate("/collectors")} size="lg" className="bg-gradient-primary text-white hover:opacity-90 shadow-elegant">
                Learn More →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8 sm:py-10 bg-muted/50 md:py-[40px]">
        <div className="mx-auto max-w-7xl px-2 sm:px-4 md:px-6 lg:px-8 py-0">
          <div className="mx-auto max-w-2xl lg:max-w-none">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  Built for responsible wildlife facilities
                </h2>
                <p className="mt-3 sm:mt-4 text-base sm:text-lg text-muted-foreground">
                  Zoocertica consolidates CITES permits, USDA licenses, state regulations, veterinary records, and transport documentation into one inspection-ready platform. Trusted by zoos, sanctuaries, aquariums, and accredited research facilities to maintain full compliance and simplify audits.
                </p>

                <div className="mt-6 sm:mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
                  {!isAuthenticated}
                  <Button variant="outline" onClick={() => navigate("/about")} size="lg" className="w-full sm:w-auto">
                    Learn More
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/contact")} size="lg" className="w-full sm:w-auto">
                    Contact Us
                  </Button>
                </div>
              </div>
              
              {/* Right column - Benefits list */}
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {benefits.map((benefit, index) => <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                    <span className="text-sm sm:text-base text-foreground font-medium">{benefit}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>;
};
export default Index;